import {Badge, Dot, Skeleton, Typography} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {FoldButton, FoldChevron} from '@/components';

import {TRUST_STATUS_GROUP_CONFIG} from '../config';

import type {TrustStatus} from '@sollapay/enums';
import type {FC} from 'react';

/** `table` sits in the table's group divider row; `section` heads a card grid and draws a hairline to the end. */
export interface TrustAccountStatusGroupHeaderProps {
  status: TrustStatus;
  count: number;
  variant?: 'table' | 'section';
  isCollapsed?: boolean;
  onToggle?: () => void;
  controlsId?: string;
}

/** With `onToggle`, the title is a fold button stretched over the whole header. */
export const TrustAccountStatusGroupHeader: FC<TrustAccountStatusGroupHeaderProps> = ({
  status,
  count,
  variant = 'table',
  isCollapsed = false,
  onToggle,
  controlsId
}) => {
  const {t} = useTranslation('trustAccounts');
  const {titleKey, subtitleKey, dotClassName} = TRUST_STATUS_GROUP_CONFIG[status];
  const title = t(titleKey);
  const isSection = variant === 'section';

  return (
    <div
      data-slot="status-group-header"
      className="group/header relative flex min-w-0 items-center gap-2.5"
    >
      {onToggle && (
        <span
          aria-hidden
          data-slot="fold-chevron-frame"
          className="flex size-5.5 shrink-0 items-center justify-center rounded-md text-fg-tertiary group-hover/header:bg-brand-secondary group-hover/header:text-fg"
        >
          <FoldChevron isCollapsed={isCollapsed} className="size-3.5" />
        </span>
      )}
      <Dot colorClassName={dotClassName} size="md" />
      {onToggle ? (
        <FoldButton
          isCollapsed={isCollapsed}
          controlsId={controlsId}
          onToggle={onToggle}
          className={isSection ? 'text-base' : 'text-sm'}
        >
          {title}
        </FoldButton>
      ) : (
        <Typography as="span" size={isSection ? 'md' : 'sm'} weight="bold">
          {title}
        </Typography>
      )}
      <Badge
        variant={isSection ? 'count' : 'surface'}
        size="sm"
        className="shrink-0 font-semibold tabular-nums text-fg-secondary"
      >
        {count}
      </Badge>
      {subtitleKey && (
        <Typography as="span" size="sm" color="tertiary" className="min-w-0">
          {t(subtitleKey)}
        </Typography>
      )}
      {isSection && (
        <span aria-hidden data-slot="hairline" className="h-px min-w-6 flex-1 bg-border-subtle" />
      )}
    </div>
  );
};

export const TrustAccountStatusGroupHeaderSkeleton: FC<
  Pick<TrustAccountStatusGroupHeaderProps, 'variant'>
> = ({variant = 'table'}) => (
  <div data-slot="status-group-header-skeleton" className="flex items-center gap-2.5">
    <Skeleton className="size-2 rounded-full" />
    <Skeleton className="h-4 w-20" />
    <Skeleton className="h-5 w-7 rounded-full" />
    {variant === 'section' && <span aria-hidden className="h-px flex-1 bg-border-subtle" />}
  </div>
);
